import React from "react";
import { Phone, Mail, MessageCircle, MapPin, Clock, ExternalLink } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { COMPANY, buildWhatsappUrl, openWhatsapp, WA_MESSAGES, phoneHref, emailHref } from "../lib/contact";
import api from "../lib/api";
import { toast } from "sonner";

const Contact = () => {
  const [form, setForm] = React.useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Bitte Name, E-Mail und Nachricht ausfüllen.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact-messages", form);
      toast.success("Nachricht gesendet. Vielen Dank!");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Senden fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[var(--na-bg)] pt-12 pb-24" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-[11px] uppercase tracking-[0.3em] text-[var(--na-red)] font-semibold mb-3">
          Kontakt · Wien
        </p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tighter">
          Sprechen Sie mit uns.
        </h1>
        <p className="text-zinc-400 mt-4 max-w-xl">
          Wir freuen uns auf Ihre Nachricht. Persönliche Termine bitte vorab telefonisch oder per WhatsApp vereinbaren.
        </p>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 mt-12">
          <div className="lg:col-span-5 space-y-4">
            <div className="bg-[#2E2E3C] border border-silver-soft rounded-2xl p-6">
              <h3 className="font-display text-lg font-semibold text-white mb-4">Adresse</h3>
              <p className="text-zinc-300 leading-relaxed text-sm">
                {COMPANY.name}<br />
                {COMPANY.legal}<br />
                {COMPANY.address}<br />
                {COMPANY.zipCity}<br />
                {COMPANY.country}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <a
                href={phoneHref}
                data-testid="contact-phone-card"
                className="bg-[#2E2E3C] border border-silver-soft hover:border-white/15 rounded-2xl p-5 group transition-colors"
              >
                <Phone className="w-5 h-5 text-[var(--na-red)] mb-3" strokeWidth={1.7} />
                <p className="text-[10px] uppercase tracking-widest text-zinc-500">Telefon</p>
                <p className="text-white font-medium mt-1 group-hover:text-[var(--na-red)] transition-colors">
                  {COMPANY.phone}
                </p>
              </a>

              <a
                href={buildWhatsappUrl(WA_MESSAGES.contact)}
                onClick={openWhatsapp(WA_MESSAGES.contact)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-card"
                className="bg-[#2E2E3C] border border-silver-soft hover:border-white/15 rounded-2xl p-5 group transition-colors"
              >
                <MessageCircle className="w-5 h-5 text-[#25D366] mb-3" strokeWidth={1.7} />
                <p className="text-[10px] uppercase tracking-widest text-zinc-500">WhatsApp</p>
                <p className="text-white font-medium mt-1 group-hover:text-[#25D366] transition-colors">
                  Chat starten
                </p>
              </a>

              <a
                href={emailHref}
                data-testid="contact-email-card"
                className="bg-[#2E2E3C] border border-silver-soft hover:border-white/15 rounded-2xl p-5 group transition-colors sm:col-span-2"
              >
                <Mail className="w-5 h-5 text-[var(--na-red)] mb-3" strokeWidth={1.7} />
                <p className="text-[10px] uppercase tracking-widest text-zinc-500">E-Mail</p>
                <p className="text-white font-medium mt-1 group-hover:text-[var(--na-red)] transition-colors">
                  {COMPANY.email}
                </p>
              </a>
            </div>

            <div className="bg-[#2E2E3C] border border-silver-soft rounded-2xl p-6">
              <h3 className="font-display text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Clock className="w-4 h-4 text-[var(--na-red)]" /> Öffnungszeiten
              </h3>
              <ul className="space-y-2 text-sm text-zinc-300">
                <li>{COMPANY.hours.weekday}</li>
                <li>{COMPANY.hours.sat}</li>
                <li>{COMPANY.hours.sun}</li>
              </ul>
            </div>

            <div className="rounded-2xl overflow-hidden border border-white/5 bg-[#232330] relative aspect-[16/9]">
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
                <MapPin className="w-8 h-8 text-[var(--na-red)] mb-3" strokeWidth={1.5} />
                <p className="font-display font-semibold text-white">{COMPANY.address}</p>
                <p className="text-sm text-zinc-400">{COMPANY.zipCity}</p>
                <a
                  href={`https://www.google.com/maps/search/${encodeURIComponent(COMPANY.address + ", " + COMPANY.zipCity)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-map-link"
                  className="mt-4 inline-flex items-center gap-2 text-sm text-[var(--na-red)] hover:underline"
                >
                  In Google Maps öffnen <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
              <div className="absolute inset-0 opacity-30" style={{
                backgroundImage: "radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)",
                backgroundSize: "16px 16px"
              }} />
            </div>
          </div>

          {/* Form */}
          <form onSubmit={submit} className="lg:col-span-7 bg-[#2E2E3C] border border-silver-soft rounded-2xl p-6 lg:p-8 space-y-5 h-fit">
            <h2 className="font-display text-2xl font-semibold text-white">Nachricht schreiben</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FInput testId="contact-form-name" label="Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <FInput testId="contact-form-email" label="E-Mail *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <FInput testId="contact-form-phone" label="Telefon" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
              <FInput testId="contact-form-subject" label="Betreff" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2 block">Nachricht *</Label>
              <textarea
                data-testid="contact-form-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={6}
                className="w-full bg-[#1A1A22] border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[var(--na-red)]"
                placeholder="Wie können wir Ihnen helfen?"
              />
            </div>
            <Button type="submit" disabled={submitting} data-testid="contact-form-submit" className="bg-[var(--na-red)] hover:bg-[var(--na-red-hover)] text-white rounded-xl tracking-wide font-medium px-6 py-3">
              {submitting ? "Wird gesendet…" : "Nachricht senden"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

const FInput = ({ label, value, onChange, testId, type = "text" }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2 block">{label}</Label>
    <Input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="bg-[#1A1A22] border-white/10 text-white focus-visible:ring-[var(--na-red)] focus-visible:border-[var(--na-red)]" />
  </div>
);

export default Contact;
